/**
 * Wired-up replay scene — the package's primary export.
 *
 * Takes a `ParsedBattle` + a few render options and renders the scene
 * at the current `tick`. All the lower-level pieces (Stage, HexFloor,
 * mech rendering, physics, debris, decals) live below; this component
 * is the conventional composition for "I want a replay viewer."
 *
 * Stories or apps can either consume `<ReplayScene>` directly or skip
 * it and compose the primitives themselves (the marketing Pixi viewer
 * will continue to live in `apps/marketing` and isn't replaced by
 * anything here — see PRD §11 OQ-1).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { Hex, ParsedBattle } from "@pilot-protocol/replay-parser";
import { HexFloor } from "./scene/HexFloor";
import { Stage, type StageProps } from "./scene/Stage";
import { DamageCallouts } from "./scene/DamageCallouts";
import { DustPuffs } from "./scene/DustPuffs";
import { FloorPulses } from "./scene/FloorPulses";
import { MovementTrails } from "./scene/MovementTrails";
import { PlaceholderMech } from "./scene/PlaceholderMech";
import { PlaybackHud } from "./scene/PlaybackHud";
import { RangeOverlay, type OverlayMode } from "./scene/RangeOverlay";
import { ArchetypeMech } from "./voxel/ArchetypeMech";
import { composeMech } from "./voxel/composer";
import type { Archetype, MaterialSlot } from "./voxel/types";
import { MatchOverlay, type OverlayPanel } from "./overlay/MatchOverlay";
import type { MatchScore } from "./overlay/MatchMetadataStrip";
import type { OverlayTokens } from "./overlay/tokens";
import type { TranscriptEntry } from "./overlay/transcript";
import { frameAt } from "./replay/adapter";
import { eventStream, eventsAtTick, type Damage, type RenderEvent } from "./replay/events";
import { PhysicsScene, KinematicMechHull } from "./physics/PhysicsScene";
import { DebrisField, type DebrisSpawnRequest } from "./physics/Debris";
import { Decals, type DecalSpec } from "./physics/Decals";
import { IMPULSE_PROFILES, REACTOR_BREACH } from "./physics/impulses";
import { WeaponFxField } from "./physics/weapons/WeaponFxField";
import { defaultFxKindForDamage, type WeaponFxSpawn } from "./physics/weapons/types";
import { hexToWorld, facingToYaw } from "./scene/hexMath";

const SIDE_COLOR_A = "#38bdf8";
const SIDE_COLOR_B = "#f97316";

export interface ReplaySceneProps {
  battle: ParsedBattle;
  tick: number;
  /**
   * "placeholder" → P1/P2 cubes (use this for lighting tuning).
   * "voxel"       → P3+ composed voxel mechs.
   */
  mechMode?: "placeholder" | "voxel";
  /** Archetype hint per side — drives voxel composition. */
  archetypeA?: Archetype;
  archetypeB?: Archetype;
  /** Cosmetic seed material — usually the match seed; defaults to 0. */
  matchSeed?: number | string;
  /** When true (P4), spawn debris/decals from events at the current tick. */
  physicsEnabled?: boolean;
  /** When true, freeze physics (pause). */
  paused?: boolean;
  /** Forwarded to `<Stage>` so stories pick lighting/postFx. */
  stage?: Omit<StageProps, "children">;
  /** A11y opt-out for motion-bearing visual effects (walk-cycle bob,
   *  tilt). Information-bearing effects (flash, fade, glow, tracers)
   *  still fire. Wires §12.12 of `voxel-cosmetics-plan.md`. */
  reducedMotion?: boolean;
  /** Show floating "−N ZONE" labels on hit events (FE-30 P3). Defaults
   *  to `true`. */
  showDamageCallouts?: boolean;
  /** Range-overlay mode (FE-30 P5). `"off"` (default) renders nothing. */
  overlay?: OverlayMode;
  /** Hex radius for A's reach overlay when `overlay="range"`. */
  overlayRangeA?: number;
  /** Hex radius for B's reach overlay when `overlay="range"`. */
  overlayRangeB?: number;
  /** Spawn cube-particle dust at the origin tile on movement (FE-30
   *  Q3). Defaults to `true`. */
  showDustPuffs?: boolean;
  /** Render the last few hex positions each mech occupied as fading
   *  discs on the floor (FE-30 P2). Default `true`. */
  showMovementTrails?: boolean;
  /** Brief tile-glow under the shooter on weapon fire (FE-30 Q2).
   *  Defaults to `true`. */
  showFloorPulses?: boolean;
  /** Spectator focus side (FE-30 Q4). When set, a hit to this side
   *  briefly pulses the vignette to telegraph "you got hit" without UI
   *  chrome. Defaults to `null` (off). */
  focusSide?: "A" | "B" | null;
  /** Show the FE-30 Q6 playback HUD (tick + optional speed picker).
   *  Defaults to `false`. */
  showHud?: boolean;
  /** Current playback speed multiplier for the HUD readout. */
  playbackSpeed?: number;
  /** Optional speed-picker callback. When provided, the HUD renders
   *  the speed-picker UI; consumer wires the actual playback speed. */
  onPlaybackSpeedChange?: (speed: number) => void;
  /**
   * O1 — DOM spectator overlay. When defined, ReplayScene wraps its
   * Canvas in a `position: relative` container and mounts a sibling
   * `<MatchOverlay>` with the listed panels. Default `undefined`
   * (no overlay — backwards compatible).
   *
   * Pass an empty array to mount the overlay root with no panels
   * (useful for chromakey-only modes that wire panels via children
   * elsewhere — O4 OBS route).
   */
  overlayPanels?: ReadonlyArray<OverlayPanel>;
  /** Per-instance overlay token overrides. */
  overlayTokens?: Partial<OverlayTokens>;
  /** Best-of-N match score (passed through to the metadata strip). */
  overlayScore?: MatchScore;
  /** Glicko rating per side (overlay metadata strip). */
  overlayRatingA?: number;
  overlayRatingB?: number;
  /** Show the numeric legend under each side's zone armor diagram. */
  overlayShowZoneLegend?: boolean;
  /** Overlay background mode — see `<MatchOverlay>` for details. */
  overlayBackground?: "normal" | "chromakey" | "transparent";
  /** O2 — agent transcripts per side; powers the `reasoningA` /
   *  `reasoningB` overlay panels. Omit when not loaded. */
  overlayTranscriptA?: ReadonlyArray<TranscriptEntry>;
  overlayTranscriptB?: ReadonlyArray<TranscriptEntry>;
  /** Show LLM stats (model / tokens / latency) on reasoning panels. */
  overlayShowReasoningStats?: boolean;
  /**
   * Equipped chassis-skin palette overrides per side. Consumer
   * resolves an Agent's equipped `chassis_skin_id` to a 4-color slot
   * map via `resolveChassisSkinPalette()` from
   * `@pilot-protocol/cosmetic-assets` and passes the result here.
   * `undefined` keeps the archetype's stock palette (backwards-
   * compatible default).
   */
  paletteOverrideA?: Partial<Record<MaterialSlot, string>>;
  paletteOverrideB?: Partial<Record<MaterialSlot, string>>;
}

export function ReplayScene({
  battle,
  tick,
  mechMode = "placeholder",
  archetypeA = "generalist",
  archetypeB = "generalist",
  matchSeed = battle.openMeta?.seed ?? 0,
  physicsEnabled = false,
  paused = true,
  stage,
  reducedMotion = false,
  showDamageCallouts = true,
  overlay = "off",
  overlayRangeA,
  overlayRangeB,
  showDustPuffs = true,
  showMovementTrails = true,
  showFloorPulses = true,
  focusSide = null,
  showHud = false,
  playbackSpeed,
  onPlaybackSpeedChange,
  overlayPanels,
  overlayTokens,
  overlayScore,
  overlayRatingA,
  overlayRatingB,
  overlayShowZoneLegend,
  overlayBackground,
  overlayTranscriptA,
  overlayTranscriptB,
  overlayShowReasoningStats,
  paletteOverrideA,
  paletteOverrideB,
}: ReplaySceneProps) {
  const frame = useMemo(() => frameAt(battle, tick), [battle, tick]);
  const stream = useMemo(() => eventStream(battle), [battle]);
  const eventsHere = useMemo(() => eventsAtTick(stream, frame.tick), [stream, frame.tick]);

  const compA = useMemo(
    () =>
      composeMech({
        matchSeed,
        side: "A",
        agentName: battle.openMeta?.agent_a?.name ?? "agent_a",
        archetype: archetypeA,
      }),
    [matchSeed, battle.openMeta?.agent_a?.name, archetypeA],
  );
  const compB = useMemo(
    () =>
      composeMech({
        matchSeed,
        side: "B",
        agentName: battle.openMeta?.agent_b?.name ?? "agent_b",
        archetype: archetypeB,
      }),
    [matchSeed, battle.openMeta?.agent_b?.name, archetypeB],
  );

  // Build cumulative decal list from kill events <= current tick.
  const decals = useMemo<DecalSpec[]>(() => {
    const out: DecalSpec[] = [];
    for (const e of stream) {
      if (e.tick > frame.tick) break;
      if (e.kind === "kill") {
        const victimMech = e.victim === "A" ? frame.state.mech_a : frame.state.mech_b;
        const w = hexToWorld(victimMech.position, 0);
        out.push({
          id: `kill-${e.tick}-${e.victim}`,
          origin: [w.x, 0, w.z],
          radius: 1.2,
          opacity: 0.85,
          color: "#0a0a0a",
        });
      }
    }
    return out;
  }, [stream, frame.tick, frame.state]);

  // Per-tick debris spawn requests for events at this tick.
  const spawnRequests = useMemo<DebrisSpawnRequest[]>(() => {
    if (!physicsEnabled) return [];
    const out: DebrisSpawnRequest[] = [];
    for (const e of eventsHere) {
      const req = buildDebrisRequest(e, frame, compA.palette.primary, compB.palette.primary);
      if (req) out.push(req);
    }
    return out;
  }, [eventsHere, frame, physicsEnabled, compA.palette.primary, compB.palette.primary]);

  // W4: per-tick weapon-FX spawn requests for `weapon_fire` + `weapon_miss`.
  // Misses use the line-of-shot prefab variants (tracer/beam/arc) regardless
  // of damageType — an explosive miss has no impact point so we substitute
  // the missile-arc prefab so the player sees the warhead overshoot.
  //
  // Every fire also spawns a muzzle_flash at the origin (P1.1) so the
  // chassis reads a punch even before the tracer/beam/arc clears the
  // barrel. Pairs with `recoilOffset` + `recoilPitch` in ArchetypeMech.
  const fxRequests = useMemo<WeaponFxSpawn[]>(() => {
    const out: WeaponFxSpawn[] = [];
    for (let i = 0; i < eventsHere.length; i++) {
      const e = eventsHere[i];
      // B12 — ammo_explosion is a catastrophic chest detonation that
      // doesn't necessarily end the match. Spawn a shockwave + muzzle-
      // flash at the chassis to read it as a major event distinct from
      // a regular kill.
      if (e.kind === "ammo_explosion") {
        const victimMech = e.victim === "A" ? frame.state.mech_a : frame.state.mech_b;
        const w = hexToWorld(victimMech.position, 0);
        out.push({
          id: e.tick * 1000 + i + 800000,
          kind: "shockwave_ring",
          origin: [w.x, 0.6, w.z],
          target: [w.x, 0.6, w.z],
          damageType: "explosive",
          seed: e.tick * 71 + i,
          tick: e.tick,
        });
        out.push({
          id: e.tick * 1000 + i + 810000,
          kind: "muzzle_flash",
          origin: [w.x, 0.7, w.z],
          target: [w.x, 0.7, w.z],
          damageType: "explosive",
          seed: e.tick * 71 + i + 1,
          tick: e.tick,
        });
        continue;
      }
      // B12 — ams_cancel intercept burst at the victim. The inbound
      // missile prefab still plays out (the engine doesn't tell us
      // which spawn id was cancelled); the intercept burst at the
      // victim signals "AMS got it" alongside.
      if (e.kind === "ams_cancel") {
        const victimMech = e.victim === "A" ? frame.state.mech_a : frame.state.mech_b;
        const w = hexToWorld(victimMech.position, 0);
        out.push({
          id: e.tick * 1000 + i + 850000,
          kind: "muzzle_flash",
          origin: [w.x, 0.9, w.z],
          target: [w.x, 0.9, w.z],
          damageType: "ballistic",
          seed: e.tick * 19 + i,
          tick: e.tick,
        });
        continue;
      }
      // B2 — shockwave_ring on kill events (REACTOR_BREACH) and on
      // explosive hits. Ground-level ring expands from the impact hex;
      // pairs with DebrisField + CA pulse.
      if (e.kind === "kill") {
        const victimMech = e.victim === "A" ? frame.state.mech_a : frame.state.mech_b;
        const w = hexToWorld(victimMech.position, 0);
        out.push({
          id: e.tick * 1000 + i + 700000,
          kind: "shockwave_ring",
          origin: [w.x, 0.6, w.z],
          target: [w.x, 0.6, w.z],
          damageType: "explosive",
          seed: e.tick * 53 + i,
          tick: e.tick,
        });
        continue;
      }
      if (
        e.kind === "hit" &&
        (e as { damageType?: Damage }).damageType === "explosive"
      ) {
        const victimMech = e.victim === "A" ? frame.state.mech_a : frame.state.mech_b;
        const w = hexToWorld(victimMech.position, 0);
        out.push({
          id: e.tick * 1000 + i + 600000,
          kind: "shockwave_ring",
          origin: [w.x, 0.6, w.z],
          target: [w.x, 0.6, w.z],
          damageType: "explosive",
          seed: e.tick * 53 + i,
          tick: e.tick,
        });
        continue;
      }
      if (e.kind !== "weapon_fire" && e.kind !== "weapon_miss") continue;
      const shooterMech = e.shooter === "A" ? frame.state.mech_a : frame.state.mech_b;
      // `e.target` is the victim SIDE ("A"|"B"); look up the target
      // mech's hex from frame state. Engine FIRE payloads don't carry
      // a target hex — only the victim player id, which we map to a
      // side in normalizeEvent.
      const targetMech = e.target === "A" ? frame.state.mech_a : frame.state.mech_b;
      const o = hexToWorld(shooterMech.position, 0);
      const t = hexToWorld(targetMech.position, 0);
      const miss = e.kind === "weapon_miss";
      const baseId = e.tick * 1000 + i;
      // Muzzle flash at the origin — short-lived (~100ms), no target.
      out.push({
        id: baseId * 2,
        kind: "muzzle_flash",
        origin: [o.x, 0.6, o.z],
        target: [o.x, 0.6, o.z],
        damageType: e.damageType,
        seed: e.tick * 31 + i,
        tick: e.tick,
        miss,
      });
      // Line-of-shot tracer / beam / arc / burst.
      out.push({
        id: baseId * 2 + 1,
        kind:
          miss && e.damageType === "explosive"
            ? "missile_arc"
            : defaultFxKindForDamage(e.damageType),
        origin: [o.x, 0.6, o.z],
        target: [t.x, 0.6, t.z],
        damageType: e.damageType,
        seed: e.tick * 31 + i,
        tick: e.tick,
        miss,
      });
    }
    return out;
  }, [eventsHere, frame.state.mech_a, frame.state.mech_b]);

  // Hit-pulse — pass 2 + B12/B13. True for one tick when a `hit` event
  // (or an ammo_explosion, treated visually as a catastrophic self-
  // hit) lands on the side this frame. B13: `collision` is two-sided
  // (both chassis ping) so it pulses both A and B regardless of victim.
  // ArchetypeMech consumes the rising edge to fire a ~220ms emissive
  // flash + scale-pulse + impulse on the victim.
  const collisionHere = useMemo(
    () => eventsHere.some((e) => e.kind === "collision"),
    [eventsHere],
  );
  const hitPulseA = useMemo(
    () =>
      collisionHere ||
      eventsHere.some(
        (e) =>
          (e.kind === "hit" || e.kind === "ammo_explosion") && e.victim === "A",
      ),
    [eventsHere, collisionHere],
  );
  const hitPulseB = useMemo(
    () =>
      collisionHere ||
      eventsHere.some(
        (e) =>
          (e.kind === "hit" || e.kind === "ammo_explosion") && e.victim === "B",
      ),
    [eventsHere, collisionHere],
  );

  // P1.2 hit normal — unit vector from the shooter's hex to the
  // victim's hex (XZ). The chassis is pushed along this vector at
  // impact. Two-mech battles only: when one side is hit, the shooter
  // is unambiguously the other side.
  const hitNormalA = useMemo(
    () => deriveHitNormal(frame.state.mech_b.position, frame.state.mech_a.position),
    [frame.state.mech_a.position, frame.state.mech_b.position],
  );
  const hitNormalB = useMemo(
    () => deriveHitNormal(frame.state.mech_a.position, frame.state.mech_b.position),
    [frame.state.mech_a.position, frame.state.mech_b.position],
  );

  // B6 hit zone — when a hit event lands here, surface its zone so
  // ArchetypeMech can flash only the visual part that maps to it.
  const hitZoneA = useMemo<string | null>(() => {
    for (const e of eventsHere) {
      if (e.kind === "hit" && e.victim === "A") return e.zone;
    }
    return null;
  }, [eventsHere]);
  const hitZoneB = useMemo<string | null>(() => {
    for (const e of eventsHere) {
      if (e.kind === "hit" && e.victim === "B") return e.zone;
    }
    return null;
  }, [eventsHere]);

  // Just-fired — P4. True for one tick when this side fired a weapon
  // (hit or miss). Drives the white-flash on the status pip.
  const justFiredA = useMemo(
    () =>
      eventsHere.some(
        (e) => (e.kind === "weapon_fire" || e.kind === "weapon_miss") && e.shooter === "A",
      ),
    [eventsHere],
  );
  const justFiredB = useMemo(
    () =>
      eventsHere.some(
        (e) => (e.kind === "weapon_fire" || e.kind === "weapon_miss") && e.shooter === "B",
      ),
    [eventsHere],
  );

  // Killed-by-current-tick — pass 3. True once a `kill` event for this
  // side has landed at any tick ≤ current. ArchetypeMech consumes the
  // rising edge to start a ~900ms body fade. Scrubbing back un-kills.
  const { killedA, killedB } = useMemo(() => {
    let a = false;
    let b = false;
    for (const e of stream) {
      if (e.tick > frame.tick) break;
      if (e.kind === "kill") {
        if (e.victim === "A") a = true;
        else if (e.victim === "B") b = true;
      }
    }
    return { killedA: a, killedB: b };
  }, [stream, frame.tick]);

  // Q4 vignette pulse — fires when the focus side takes a hit at the
  // current tick. Pure wall-clock animation in PostFx.
  const [vignettePulseStartedAt, setVignettePulseStartedAt] = useState<number | null>(null);
  const prevFocusHitRef = useRef<boolean>(false);
  useEffect(() => {
    if (!focusSide) {
      prevFocusHitRef.current = false;
      return;
    }
    const focusHit = focusSide === "A" ? hitPulseA : hitPulseB;
    if (focusHit && !prevFocusHitRef.current) {
      setVignettePulseStartedAt(performance.now());
    }
    prevFocusHitRef.current = focusHit;
  }, [focusSide, hitPulseA, hitPulseB]);

  // P6 kill-light choreography + Q5 camera shake — rising-edge tracker.
  // Re-fires when either side transitions from alive → dead. The
  // `seed` captures the kill *tick* for the camera-shake direction so
  // two viewings of the same replay produce the same shake.
  const [killPulseStartedAt, setKillPulseStartedAt] = useState<number | null>(null);
  const [killShakeSeed, setKillShakeSeed] = useState<number>(0);
  const prevAnyKilledRef = useRef<boolean>(false);
  useEffect(() => {
    const anyKilled = killedA || killedB;
    if (anyKilled && !prevAnyKilledRef.current) {
      setKillPulseStartedAt(performance.now());
      // Find the latest kill tick ≤ current to seed the shake direction.
      let killTick = frame.tick;
      for (const e of stream) {
        if (e.tick > frame.tick) break;
        if (e.kind === "kill") killTick = e.tick;
      }
      setKillShakeSeed(killTick >>> 0);
    }
    prevAnyKilledRef.current = anyKilled;
  }, [killedA, killedB, frame.tick, stream]);

  const mechA = frame.mechA;
  const mechB = frame.mechB;

  const worldA = hexToWorld(mechA.position, 0);
  const worldB = hexToWorld(mechB.position, 0);

  const sceneContent = (
    <>
      <HexFloor state={frame.state} />
      <RangeOverlay
        mode={overlay}
        posA={mechA.position}
        posB={mechB.position}
        rangeA={overlayRangeA}
        rangeB={overlayRangeB}
      />
      {showMovementTrails ? (
        <MovementTrails
          battle={battle}
          currentTick={frame.tick}
          colorA={SIDE_COLOR_A}
          colorB={SIDE_COLOR_B}
        />
      ) : null}
      {showFloorPulses ? (
        <FloorPulses eventsHere={eventsHere} state={frame.state} tick={frame.tick} />
      ) : null}
      {showDustPuffs ? (
        <DustPuffs
          posA={mechA.position}
          posB={mechB.position}
          tick={frame.tick}
          matchSeed={matchSeed}
          disabled={reducedMotion}
        />
      ) : null}
      <Decals decals={decals} />
      {mechMode === "placeholder" ? (
        <>
          <PlaceholderMech mech={mechA} color={SIDE_COLOR_A} />
          <PlaceholderMech mech={mechB} color={SIDE_COLOR_B} />
        </>
      ) : (
        <>
          <ArchetypeMech
            mech={mechA}
            composition={compA}
            paletteOverride={paletteOverrideA}
            hitPulse={hitPulseA}
            hitNormal={hitNormalA}
            hitZone={hitZoneA}
            killed={killedA}
            justFired={justFiredA}
            reducedMotion={reducedMotion}
          />
          <ArchetypeMech
            mech={mechB}
            composition={compB}
            paletteOverride={paletteOverrideB}
            hitPulse={hitPulseB}
            hitNormal={hitNormalB}
            hitZone={hitZoneB}
            killed={killedB}
            justFired={justFiredB}
            reducedMotion={reducedMotion}
          />
        </>
      )}
    </>
  );

  const stageNode = (
    <Stage
      {...(stage ?? {})}
      killPulseStartedAt={killPulseStartedAt}
      cameraShakeSeed={killShakeSeed}
      reducedMotion={reducedMotion}
      vignettePulseStartedAt={vignettePulseStartedAt}
      hitPulseStartedAt={vignettePulseStartedAt}
    >
      {physicsEnabled ? (
        <PhysicsScene paused={paused}>
          {sceneContent}
          <KinematicMechHull
            position={[worldA.x, 0.5, worldA.z]}
            // rotation handled via tick-state in the visible mech;
            // the hull is a coarse cuboid for collisions only.
          />
          <KinematicMechHull position={[worldB.x, 0.5, worldB.z]} />
          <DebrisField requests={spawnRequests} />
          <WeaponFxField requests={fxRequests} />
          {showDamageCallouts ? (
            <DamageCallouts eventsHere={eventsHere} state={frame.state} tick={frame.tick} />
          ) : null}
        </PhysicsScene>
      ) : (
        <>
          {sceneContent}
          <WeaponFxField requests={fxRequests} />
          {showDamageCallouts ? (
            <DamageCallouts eventsHere={eventsHere} state={frame.state} tick={frame.tick} />
          ) : null}
        </>
      )}
      {showHud ? (
        <PlaybackHud
          currentTick={frame.tick}
          maxTick={battle.states.length - 1}
          speed={playbackSpeed}
          onSpeedChange={onPlaybackSpeedChange}
        />
      ) : null}
    </Stage>
  );

  // O1 — DOM overlay lives as a sibling to the Canvas. When the
  // consumer asks for any panel, wrap the stage in a position-relative
  // container so the overlay can anchor to its bounds. When no overlay
  // is requested, return the stage bare to keep the no-overlay tree
  // identical to the legacy shape.
  if (overlayPanels === undefined) return stageNode;
  return (
    <div style={replaySceneRootStyle}>
      {stageNode}
      <MatchOverlay
        battle={battle}
        tick={frame.tick}
        panels={overlayPanels}
        tokens={overlayTokens}
        score={overlayScore}
        ratingA={overlayRatingA}
        ratingB={overlayRatingB}
        archetypeA={archetypeA}
        archetypeB={archetypeB}
        showZoneLegend={overlayShowZoneLegend}
        background={overlayBackground}
        transcriptA={overlayTranscriptA}
        transcriptB={overlayTranscriptB}
        showReasoningStats={overlayShowReasoningStats}
      />
    </div>
  );
}

const replaySceneRootStyle: React.CSSProperties = {
  position: "relative",
  width: "100%",
  height: "100%",
};

function buildDebrisRequest(
  e: RenderEvent,
  frame: ReturnType<typeof frameAt>,
  colorA: string,
  colorB: string,
): DebrisSpawnRequest | null {
  // B12: ammo_explosion uses the same REACTOR_BREACH profile as kill
  // (catastrophic chunk burst). The victim isn't dead from this event
  // alone, so we still drop chunks.
  const isExplosive =
    e.kind === "hit" || e.kind === "kill" || e.kind === "ammo_explosion";
  if (!isExplosive) return null;
  const victim = (e as { victim?: "A" | "B" }).victim;
  if (!victim) return null;
  const victimMech = victim === "A" ? frame.state.mech_a : frame.state.mech_b;
  const w = hexToWorld(victimMech.position, 0);
  const color = victim === "A" ? colorA : colorB;
  const profile =
    e.kind === "kill" || e.kind === "ammo_explosion"
      ? REACTOR_BREACH
      : IMPULSE_PROFILES[(e as { damageType?: Damage }).damageType ?? "other"];
  // Approximate normal: away from the victim's facing.
  const yaw = facingToYaw(victimMech.facing);
  const normal: [number, number, number] = [Math.sin(yaw), 0.5, Math.cos(yaw)];
  return {
    id:
      e.tick * 1000 +
      (e.kind === "kill" ? 0 : e.kind === "ammo_explosion" ? 2 : 1),
    origin: [w.x, 0.5, w.z],
    seed:
      frame.tick +
      (e.kind === "kill"
        ? 0xc0ffee
        : e.kind === "ammo_explosion"
          ? 0xa11_70
          : 0x0011),
    normal,
    profile,
    color,
  };
}

/**
 * Unit-vector hit normal from `shooter` to `victim` in XZ world space.
 * Returns `{dx: 0, dz: 0}` when the two positions coincide (fallback
 * — chassis still scale-pulses + flashes without a push).
 */
function deriveHitNormal(shooter: Hex, victim: Hex): { dx: number; dz: number } {
  const s = hexToWorld(shooter, 0);
  const v = hexToWorld(victim, 0);
  const dx = v.x - s.x;
  const dz = v.z - s.z;
  const mag = Math.hypot(dx, dz);
  if (mag < 1e-6) return { dx: 0, dz: 0 };
  return { dx: dx / mag, dz: dz / mag };
}
